"use client"

import * as React from "react"
import { Tooltip as TooltipPrimitive } from "radix-ui"

import { cn } from "@/lib/utils"

const TooltipOpenContext = React.createContext(null)

function TooltipProvider({
  delayDuration = 0,
  ...props
}) {
  return (<TooltipPrimitive.Provider data-slot="tooltip-provider" delayDuration={delayDuration} {...props} />);
}

function Tooltip({
  open: controlledOpen,
  onOpenChange,
  ...props
}) {
  const [open, setOpen] = React.useState(false)
  const isControlled = controlledOpen !== undefined

  const handleOpenChange = (nextOpen) => {
    if (!isControlled) setOpen(nextOpen)
    if (onOpenChange) onOpenChange(nextOpen)
  }

  return (
    <TooltipPrimitive.Root
      data-slot="tooltip"
      open={isControlled ? controlledOpen : open}
      onOpenChange={handleOpenChange}
      {...props}
    >
      <TooltipOpenContext.Provider value={setOpen}>
        {props.children}
      </TooltipOpenContext.Provider>
    </TooltipPrimitive.Root>
  )
}

const TooltipTrigger = React.forwardRef(({ onClick, ...props }, ref) => {
  const setOpen = React.useContext(TooltipOpenContext)

  const handleClick = (event) => {
    if (onClick) onClick(event)
    if (setOpen) {
      setOpen((current) => !current)
    }
  }

  return <TooltipPrimitive.Trigger ref={ref} data-slot="tooltip-trigger" onClick={handleClick} {...props} />;
})

TooltipTrigger.displayName = 'TooltipTrigger'

function TooltipContent({
  className,
  sideOffset = 0,
  children,
  ...props
}) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          "z-50 inline-flex w-fit max-w-xs origin-(--radix-tooltip-content-transform-origin) items-center gap-1.5 rounded-none bg-foreground px-3 py-1.5 text-xs text-background has-data-[slot=kbd]:pr-1.5 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 **:data-[slot=kbd]:relative **:data-[slot=kbd]:isolate **:data-[slot=kbd]:z-50 **:data-[slot=kbd]:rounded-none data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:zoom-in-95 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95",
          className
        )}
        {...props}>
        {children}
        <TooltipPrimitive.Arrow
          className="z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-none bg-foreground fill-foreground" />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger }
